import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './hooks/useAuth';

interface DeveloperBreadcrumbContextType {
  isDevModeEnabled: boolean;
  toggleDevMode: () => void;
  canAccessDevMode: boolean;
}

const DeveloperBreadcrumbContext = createContext<DeveloperBreadcrumbContextType | undefined>(undefined);

const DEV_MODE_STORAGE_KEY = 'outhere_dev_mode_enabled';

// Use sessionStorage rather than localStorage so the flag is scoped to
// the current browser tab and clears automatically on tab close. Avoids
// the shared-device foothold where an admin's dev-mode flag persists
// after they sign out and could be re-activated by a non-admin who
// later signs in. The UI gates dev mode on `canAccessDevMode` so the
// flag is never directly exploitable, but a leftover-flag is a
// social-engineering / debugging-context leak. SECURITY_AUDIT.md M7.
function readDevModeFlag(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(DEV_MODE_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeDevModeFlag(enabled: boolean) {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(DEV_MODE_STORAGE_KEY, enabled.toString());
  } catch {
    // sessionStorage may be unavailable (e.g. private mode quotas);
    // ignore and let the in-memory state hold.
  }
}

function clearLegacyLocalStorageFlag() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(DEV_MODE_STORAGE_KEY);
  } catch {
    // Same fallback as above.
  }
}

export const DeveloperBreadcrumbProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, isAdmin } = useAuth();
  const canAccessDevMode = Boolean(user) && isAdmin;

  // Initialize from sessionStorage only if user can access dev mode.
  const [isDevModeEnabled, setIsDevModeEnabled] = useState(() => {
    if (!canAccessDevMode) return false;
    return readDevModeFlag();
  });

  // Update sessionStorage when state changes; also wipe any legacy
  // localStorage value left over from before this migration.
  useEffect(() => {
    clearLegacyLocalStorageFlag();
    if (canAccessDevMode) {
      writeDevModeFlag(isDevModeEnabled);
    }
  }, [isDevModeEnabled, canAccessDevMode]);

  // Reset dev mode if user changes and new user can't access it
  useEffect(() => {
    if (!canAccessDevMode && isDevModeEnabled) {
      setIsDevModeEnabled(false);
    }
  }, [canAccessDevMode, isDevModeEnabled]);

  const toggleDevMode = () => {
    if (canAccessDevMode) {
      setIsDevModeEnabled(prev => !prev);
    }
  };

  return (
    <DeveloperBreadcrumbContext.Provider value={{
      isDevModeEnabled: canAccessDevMode && isDevModeEnabled,
      toggleDevMode,
      canAccessDevMode
    }}>
      {children}
    </DeveloperBreadcrumbContext.Provider>
  );
};

export const useDevMode = () => {
  const context = useContext(DeveloperBreadcrumbContext);
  if (context === undefined) {
    throw new Error('useDevMode must be used within a DeveloperBreadcrumbProvider');
  }
  return context;
};
